import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { functionsService } from '@/services/functionsService';

const pricingPlans = [
  {
    name: 'Free',
    price: 0,
    credits: 50,
    features: [
      '50 credits per day',
      'Basic transcript analysis',
      'Basic filing analysis',
      'Access to news feed',
      'Market movers',
      'Basic rankings',
    ],
    color: 'gray',
  },
  {
    name: 'Premium',
    price: 29,
    credits: 200,
    features: [
      '200 credits per day',
      'Advanced transcript analysis',
      'Advanced filing analysis',
      'Priority news summaries',
      'Full rankings access',
      'Investor Copilot (3 credits/question)',
      'Saved analyses',
      'Email support',
    ],
    color: 'green',
  },
  {
    name: 'Enterprise',
    price: 99,
    credits: 1000,
    features: [
      '1000 credits per day',
      'Unlimited analyses',
      'Custom AI prompts',
      'API access',
      'Priority support',
      'Custom integrations',
      'Dedicated account manager',
      'Advanced analytics',
    ],
    color: 'blue',
  },
];

const planToPlanId: Record<string, string> = {
  Free: 'free',
  Premium: 'premium',
  Enterprise: 'enterprise',
};

export const Pricing: React.FC = () => {
  const { user } = useAuth();
  const { tier, refreshBalance } = useCredits();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    if (success === '1') {
      setSuccessMessage('Thank you! Your subscription is active. Credits have been added.');
      refreshBalance();
      // Webhook may arrive a few seconds after redirect; refresh again so UI shows updated plan
      const t1 = setTimeout(refreshBalance, 2000);
      const t2 = setTimeout(refreshBalance, 5000);
      window.history.replaceState({}, '', '/pricing');
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else if (canceled === '1') {
      setSuccessMessage(null);
    }
  }, [searchParams, refreshBalance]);

  const handleUpgrade = async (planName: string) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    const planId = planToPlanId[planName];
    if (planId === 'free' || !planId) {
      return;
    }

    setLoading(true);
    try {
      const base = window.location.origin;
      const { url } = await functionsService.createCheckoutSession(
        planId,
        `${base}/pricing?success=1&session_id={CHECKOUT_SESSION_ID}`,
        `${base}/pricing?canceled=1`
      );
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (e: any) {
      console.error('Checkout error:', e);
      const msg = e?.message || e?.code || 'Checkout failed. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        {successMessage && (
          <div className="mb-6 mx-auto max-w-xl rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-center text-green-800">
            {successMessage}
          </div>
        )}
        <div className="text-center mb-12">
          <h1 className="h1 text-4xl mb-4">Choose Your Plan</h1>
          <p className="body-text text-gray-600 max-w-2xl mx-auto">
            Select the plan that best fits your needs. All plans include daily credit resets and access to our AI-powered analysis tools.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingPlans.map((plan) => {
            const isCurrentPlan = tier === plan.name.toLowerCase();
            const isPopular = plan.name === 'Premium';

            return (
              <Card
                key={plan.name}
                className={`relative ${isPopular ? 'ring-2 ring-blazerex-green' : ''}`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blazerex-green text-blazerex-black px-4 py-1 rounded-full text-sm font-header-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="sub-header text-2xl mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="h1 text-4xl">${plan.price}</span>
                    <span className="body-text text-gray-600">/month</span>
                  </div>
                  <p className="body-text text-sm text-gray-600">
                    {plan.credits} credits per day
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-blazerex-green mt-1">✓</span>
                      <span className="body-text text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={isCurrentPlan ? 'outline' : 'primary'}
                  className="w-full"
                  onClick={() => handleUpgrade(plan.name)}
                  disabled={isCurrentPlan || loading}
                >
                  {loading ? 'Redirecting…' : isCurrentPlan ? 'Current Plan' : plan.price === 0 ? 'Get Started' : 'Upgrade Now'}
                </Button>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="body-text text-gray-600 mb-4">
            Need a custom plan? Contact us for enterprise solutions.
          </p>
          <Button variant="outline" onClick={() => navigate('/contact')}>
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
};

